import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { combineLatest, interval } from 'rxjs';
import { finalize, startWith, switchMap } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { Card } from 'primeng/card';
import { Message } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { Tag } from 'primeng/tag';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import {
  WebAnalyticsDashboardFilters,
  WebAnalyticsService,
} from '../../services/web-analytics.service';
import { WebAnalyticsUiService } from '../../services/web-analytics-ui.service';
import {
  AnalyticsViewId,
  WebAnalyticsBreakdownRow,
  WebAnalyticsDailyEventTypeCount,
  WebAnalyticsActiveNowData,
  WebAnalyticsDashboardData,
  WebAnalyticsDashboardPeriodComparison,
  WebAnalyticsNavigationFlow,
} from '../../models/web-analytics-dashboard';
import { ColorblindService } from '../../services/colorblind.service';
import { ThemeService } from '../../services/theme.service';
import {
  getCategoricalChartPalette,
  getChartColors,
} from '../../theme/design-tokens';
import { dashboardFiltersFromQuery, queryParamsFromDashboardFilters } from '../../utils/web-analytics-route-sync';
import {
  humanizeAnalyticsPath,
  truncatePathMiddle,
} from '../../utils/path-labels';
import { formatCountryChartLabel } from '../../utils/country-flags';
import { CountryLabelComponent } from '../../components/country-label/country-label.component';

type ActivityMode = 'stacked' | 'lines' | 'total';
type Granularity = 'daily' | 'weekly' | 'hourly';

@Component({
  selector: 'app-dashboard',
  imports: [
    CommonModule,
    RouterLink,
    ButtonModule,
    Card,
    Message,
    ProgressSpinnerModule,
    SkeletonModule,
    Tag,
    ChartModule,
    TableModule,
    TooltipModule,
    CountryLabelComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  private readonly webAnalyticsService = inject(WebAnalyticsService);
  private readonly themeService = inject(ThemeService);
  private readonly colorblindService = inject(ColorblindService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly waUi = inject(WebAnalyticsUiService);

  readonly views: { id: AnalyticsViewId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'audience', label: 'Audience' },
    { id: 'technology', label: 'Technology' },
    { id: 'performance', label: 'Performance' },
    { id: 'api', label: 'API activity' },
  ];

  readonly activeView = signal<AnalyticsViewId>('overview');
  readonly activityMode = signal<ActivityMode>('stacked');
  readonly activityGranularity = signal<Granularity>('daily');
  readonly eventOtherExpanded = signal(false);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly dashboard = signal<WebAnalyticsDashboardData | null>(null);
  readonly previousDashboard = signal<WebAnalyticsDashboardData | null>(null);
  readonly activeFilters = signal<WebAnalyticsDashboardFilters | null>(null);

  readonly activeNow = signal<WebAnalyticsActiveNowData | null>(null);
  readonly activeNowLoading = signal(false);
  readonly activeNowError = signal<string | null>(null);

  readonly kpiDelta = signal<Record<string, string>>({});

  readonly activityMainData = signal<Record<string, unknown> | null>(null);
  readonly activityMainOptions = signal<Record<string, unknown> | null>(null);
  readonly eventTypeHorizData = signal<Record<string, unknown> | null>(null);
  readonly eventTypeHorizOptions = signal<Record<string, unknown> | null>(null);

  readonly sparkTotal = signal<{
    data: Record<string, unknown>;
    options: Record<string, unknown>;
  } | null>(null);
  readonly sparkPage = signal<{
    data: Record<string, unknown>;
    options: Record<string, unknown>;
  } | null>(null);
  readonly sparkApi = signal<{
    data: Record<string, unknown>;
    options: Record<string, unknown>;
  } | null>(null);
  readonly sparkSess = signal<{
    data: Record<string, unknown>;
    options: Record<string, unknown>;
  } | null>(null);
  readonly sparkUsers = signal<{
    data: Record<string, unknown>;
    options: Record<string, unknown>;
  } | null>(null);

  readonly lineChartData = signal<Record<string, unknown> | null>(null);
  readonly lineChartOptions = signal<Record<string, unknown> | null>(null);
  readonly typeChartData = signal<Record<string, unknown> | null>(null);
  readonly typeChartOptions = signal<Record<string, unknown> | null>(null);
  readonly pathsChartData = signal<Record<string, unknown> | null>(null);
  readonly pathsChartOptions = signal<Record<string, unknown> | null>(null);

  readonly countryChartData = signal<Record<string, unknown> | null>(null);
  readonly countryChartOptions = signal<Record<string, unknown> | null>(null);
  readonly browserChartData = signal<Record<string, unknown> | null>(null);
  readonly browserChartOptions = signal<Record<string, unknown> | null>(null);
  readonly osBarChartData = signal<Record<string, unknown> | null>(null);
  readonly osBarChartOptions = signal<Record<string, unknown> | null>(null);
  readonly deviceBarChartData = signal<Record<string, unknown> | null>(null);
  readonly deviceBarChartOptions = signal<Record<string, unknown> | null>(null);
  readonly cityBarChartData = signal<Record<string, unknown> | null>(null);
  readonly cityBarChartOptions = signal<Record<string, unknown> | null>(null);
  readonly usernameBarChartData = signal<Record<string, unknown> | null>(null);
  readonly usernameBarChartOptions = signal<Record<string, unknown> | null>(null);

  readonly performanceChartData = signal<Record<string, unknown> | null>(null);
  readonly performanceChartOptions = signal<Record<string, unknown> | null>(null);

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refresh());

    this.route.queryParamMap
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() =>
          interval(30_000).pipe(startWith(0)),
        ),
      )
      .subscribe(() => this.refreshActiveNow());

    combineLatest([
      this.themeService.theme$,
      this.colorblindService.colorblindMode$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const d = this.dashboard();
        if (d) {
          this.applyChartState(d);
        }
      });
  }

  setView(id: AnalyticsViewId): void {
    this.activeView.set(id);
  }

  setActivityMode(m: ActivityMode): void {
    this.activityMode.set(m);
    const d = this.dashboard();
    if (d) {
      this.buildActivityMainChart(d);
    }
  }

  setGranularity(g: Granularity): void {
    this.activityGranularity.set(g);
    const d = this.dashboard();
    if (d) {
      this.buildActivityMainChart(d);
    }
  }

  apiTrafficSharePercent(d: WebAnalyticsDashboardData): string {
    if (!d.totalEvents) {
      return '0';
    }
    return ((d.apiRequests / d.totalEvents) * 100).toFixed(1);
  }

  performanceSampleCount(d: WebAnalyticsDashboardData): number {
    return d.performanceByDay?.reduce((sum, b) => sum + b.count, 0) ?? 0;
  }

  performanceRangeAvg(
    d: WebAnalyticsDashboardData,
    key: 'avgLcp' | 'avgCls' | 'avgFcp' | 'avgTtfb',
  ): string {
    const rows = (d.performanceByDay ?? []).filter(
      (b) => b[key] != null && b.count > 0,
    );
    if (!rows.length) {
      return '—';
    }
    const weighted =
      rows.reduce((sum, b) => sum + (b[key] as number) * b.count, 0) /
      rows.reduce((sum, b) => sum + b.count, 0);
    return key === 'avgCls'
      ? weighted.toFixed(3)
      : `${Math.round(weighted)} ms`;
  }

  formatPerformanceMetric(
    key: 'avgLcp' | 'avgCls' | 'avgFcp' | 'avgTtfb',
    value?: number | null,
  ): string {
    if (value == null) {
      return '—';
    }
    return key === 'avgCls' ? value.toFixed(3) : `${Math.round(value)} ms`;
  }

  performancePathLabel(path: string): string {
    return humanizeAnalyticsPath(path).label;
  }

  topPagesWithLabels(d: WebAnalyticsDashboardData): {
    path: string;
    label: string;
    count: number;
    pct: number;
  }[] {
    const total = d.totalEvents || 1;
    return d.topPaths.map((p) => ({
      path: p.path,
      label: humanizeAnalyticsPath(p.path).label,
      count: p.count,
      pct: (p.count / total) * 100,
    }));
  }

  flowDisplayLabel(flow: WebAnalyticsNavigationFlow): string {
    return flow.steps
      .map((s) => humanizeAnalyticsPath(s).label)
      .join(' → ');
  }

  goUsers(): void {
    void this.router.navigate(['/users'], {
      queryParams: this.route.snapshot.queryParams,
    });
  }

  navigateFilterPath(path: string): void {
    this.navigateQueryPatch({ filterPath: path });
  }

  navigateFilterEventType(et: string): void {
    this.navigateQueryPatch({ filterEventType: et });
  }

  private navigateQueryPatch(patch: Partial<WebAnalyticsDashboardFilters>): void {
    const cur =
      dashboardFiltersFromQuery(this.route.snapshot.queryParamMap) ??
      this.activeFilters();
    if (!cur) {
      return;
    }
    const next: WebAnalyticsDashboardFilters = { ...cur, ...patch };
    void this.router.navigate([], {
      queryParams: queryParamsFromDashboardFilters(next),
      queryParamsHandling: '',
      });
  }

  activeNowScopeLabel(): string {
    const appId = (this.activeFilters()?.appId ?? '').trim();
    return appId === '' ? 'All apps' : appId;
  }

  private refreshActiveNow(): void {
    const f = dashboardFiltersFromQuery(this.route.snapshot.queryParamMap);
    if (!f) {
      this.activeNow.set(null);
      this.activeNowError.set(null);
      return;
    }
    this.activeNowLoading.set(true);
    this.activeNowError.set(null);
    this.webAnalyticsService
      .getActiveNow(f.appId)
      .pipe(finalize(() => this.activeNowLoading.set(false)))
      .subscribe({
        next: (data) => this.activeNow.set(data),
        error: (err: unknown) => {
          this.activeNow.set(null);
          this.activeNowError.set(
            err instanceof Error ? err.message : 'Could not load live activity.',
          );
        },
      });
  }

  private refresh(): void {
    const f = dashboardFiltersFromQuery(this.route.snapshot.queryParamMap);
    if (!f?.startDate || !f.endDate) {
      this.error.set('Choose a date range in the sidebar, then Apply.');
      this.loading.set(false);
      return;
    }
    this.activeFilters.set(f);
    this.loading.set(true);
    this.error.set(null);

    this.webAnalyticsService
      .getDashboard(f, { includePreviousPeriod: true })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (current) => {
          this.previousDashboard.set(null);
          this.kpiDelta.set(
            this.computeKpiDeltas(current, current?.previousPeriod ?? null),
          );
          this.dashboard.set(current);
          if (current) {
            this.applyChartState(current);
          } else {
            this.clearCharts();
          }
        },
        error: (err: unknown) => {
          console.error('[DashboardComponent]', err);
          this.error.set(
            err instanceof Error ? err.message : 'Could not load dashboard data.',
          );
          this.clearCharts();
        },
      });
  }

  private computeKpiDeltas(
    cur: WebAnalyticsDashboardData | null,
    prev: WebAnalyticsDashboardPeriodComparison | null,
  ): Record<string, string> {
    if (!cur || !prev) {
      return {};
    }
    const pct = (a: number, b: number) => {
      if (b <= 0) {
        return a > 0 ? '+100%' : '0%';
      }
      const p = Math.round(((a - b) / b) * 1000) / 10;
      const sign = p > 0 ? '+' : '';
      return `${sign}${p}%`;
    };
    return {
      total: pct(cur.totalEvents, prev.totalEvents),
      page: pct(cur.pageViews, prev.pageViews),
      api: pct(cur.apiRequests, prev.apiRequests),
      sess: pct(cur.uniqueSessions, prev.uniqueSessions),
      users: pct(cur.uniqueUsernames, prev.uniqueUsernames),
    };
  }

  private clearCharts(): void {
    this.activityMainData.set(null);
    this.activityMainOptions.set(null);
    this.eventTypeHorizData.set(null);
    this.eventTypeHorizOptions.set(null);
    this.sparkTotal.set(null);
    this.sparkPage.set(null);
    this.sparkApi.set(null);
    this.sparkSess.set(null);
    this.sparkUsers.set(null);
    this.lineChartData.set(null);
    this.lineChartOptions.set(null);
    this.typeChartData.set(null);
    this.typeChartOptions.set(null);
    this.pathsChartData.set(null);
    this.pathsChartOptions.set(null);
    this.countryChartData.set(null);
    this.countryChartOptions.set(null);
    this.browserChartData.set(null);
    this.browserChartOptions.set(null);
    this.osBarChartData.set(null);
    this.osBarChartOptions.set(null);
    this.deviceBarChartData.set(null);
    this.deviceBarChartOptions.set(null);
    this.cityBarChartData.set(null);
    this.cityBarChartOptions.set(null);
    this.usernameBarChartData.set(null);
    this.usernameBarChartOptions.set(null);
  }

  private applyChartState(d: WebAnalyticsDashboardData): void {
    const isDark = document.documentElement.classList.contains('theme-dark');
    const isCb = document.documentElement.classList.contains('colorblind-mode');
    const text = isDark ? '#e5e7eb' : '#374151';
    const grid = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    const baseOpts = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: { color: text },
        },
      },
      scales: {},
    };

    const labels = d.eventsByDay.map((b) => b.date.slice(5));
    const counts = d.eventsByDay.map((b) => b.count);
    const c = getChartColors(isCb);

    this.lineChartData.set({
      labels,
      datasets: [
        {
          label: 'Events',
          data: counts,
          fill: true,
          tension: 0.35,
          borderColor: c.info.border,
          backgroundColor: this.hexToRgba(c.info.primary, isDark ? 0.22 : 0.15),
          pointBackgroundColor: c.info.primary,
          pointBorderColor: isDark ? '#1f2937' : '#fff',
          pointRadius: 3,
          pointHoverRadius: 5,
        },
      ],
    });
    this.lineChartOptions.set({
      ...baseOpts,
      scales: {
        x: {
          ticks: { color: text, maxRotation: 0 },
          grid: { color: grid },
        },
        y: {
          beginAtZero: true,
          ticks: { color: text, precision: 0 },
          grid: { color: grid },
        },
      },
    });

    const types = d.eventTypeBreakdown;
    const palette = this.expandedPalette(isCb, types.length);
    this.typeChartData.set({
      labels: types.map((t) => t.eventType),
      datasets: [
        {
          data: types.map((t) => t.count),
          backgroundColor: palette.bg,
          hoverBackgroundColor: palette.hover,
          borderWidth: 0,
        },
      ],
    });
    this.typeChartOptions.set({
      ...baseOpts,
      plugins: {
        legend: {
          position: 'right',
          labels: { color: text, boxWidth: 12 },
        },
      },
    });

    const paths = [...d.topPaths].reverse();
    this.pathsChartData.set({
      labels: paths.map((p) => this.truncatePath(p.path, 48)),
      datasets: [
        {
          label: 'Views',
          data: paths.map((p) => p.count),
          backgroundColor: this.hexToRgba(
            c.secondary.primary,
            isDark ? 0.45 : 0.35,
          ),
          borderColor: c.secondary.border,
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    });
    this.pathsChartOptions.set({
      indexAxis: 'y',
      ...baseOpts,
      scales: {
        x: {
          beginAtZero: true,
          ticks: { color: text, precision: 0 },
          grid: { color: grid },
        },
        y: {
          ticks: { color: text },
          grid: { display: false },
        },
      },
    });

    const country = this.makeDoughnut(
      d.topCountries.slice(0, 14),
      isCb,
      text,
      formatCountryChartLabel,
    );
    this.countryChartData.set(country.data);
    this.countryChartOptions.set(country.options);

    const browser = this.makeDoughnut(d.topBrowsers.slice(0, 12), isCb, text);
    this.browserChartData.set(browser.data);
    this.browserChartOptions.set(browser.options);

    const osB = this.makeHorizontalBar(
      d.topOs,
      isCb,
      text,
      grid,
      'success',
    );
    this.osBarChartData.set(osB.data);
    this.osBarChartOptions.set(osB.options);

    const devB = this.makeHorizontalBar(
      d.topDevices,
      isCb,
      text,
      grid,
      'warning',
    );
    this.deviceBarChartData.set(devB.data);
    this.deviceBarChartOptions.set(devB.options);

    const cityB = this.makeHorizontalBar(
      d.topCities,
      isCb,
      text,
      grid,
      'secondary',
    );
    this.cityBarChartData.set(cityB.data);
    this.cityBarChartOptions.set(cityB.options);

    const userB = this.makeHorizontalBar(
      d.topUsernames,
      isCb,
      text,
      grid,
      'info',
    );
    this.usernameBarChartData.set(userB.data);
    this.usernameBarChartOptions.set(userB.options);

    this.buildOverviewExtras(d, isDark, isCb, text, grid);
  }

  private buildOverviewExtras(
    d: WebAnalyticsDashboardData,
    isDark: boolean,
    isCb: boolean,
    text: string,
    grid: string,
  ): void {
    this.buildSparklines(d, isDark, isCb);
    this.buildEventTypeHorizontal(d, isDark, isCb, text, grid);
    this.buildActivityMainChart(d);
    this.buildPerformanceChart(d, isDark, isCb, text, grid);
  }

  private buildSparklines(
    d: WebAnalyticsDashboardData,
    isDark: boolean,
    isCb: boolean,
  ): void {
    const k = d.kpiByDay ?? [];
    if (!k.length) {
      this.sparkTotal.set(null);
      this.sparkPage.set(null);
      this.sparkApi.set(null);
      this.sparkSess.set(null);
      this.sparkUsers.set(null);
      return;
    }
    const labels = k.map((b) => b.date.slice(5));
    const col = getChartColors(isCb);
    const mk = (vals: number[], color: string) =>
      this.miniLineChart(labels, vals, color, isDark);
    this.sparkTotal.set(mk(k.map((b) => b.totalEvents), col.info.primary));
    this.sparkPage.set(mk(k.map((b) => b.pageViews), col.success.primary));
    this.sparkApi.set(mk(k.map((b) => b.apiRequests), col.warning.primary));
    this.sparkSess.set(mk(k.map((b) => b.uniqueSessions), col.secondary.primary));
    this.sparkUsers.set(mk(k.map((b) => b.namedUserEvents), col.danger.primary));
  }

  private miniLineChart(
    labels: string[],
    data: number[],
    color: string,
    isDark: boolean,
  ): {
    data: Record<string, unknown>;
    options: Record<string, unknown>;
  } {
    return {
      data: {
        labels,
        datasets: [
          {
            data,
            borderColor: color,
            backgroundColor: this.hexToRgba(color, isDark ? 0.2 : 0.12),
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            borderWidth: 1.5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: true } },
        scales: {
          x: { display: false },
          y: { display: false, beginAtZero: true },
        },
      },
    };
  }

  private buildEventTypeHorizontal(
    d: WebAnalyticsDashboardData,
    isDark: boolean,
    isCb: boolean,
    text: string,
    grid: string,
  ): void {
    const total = d.totalEvents || 1;
    const rows = [...d.eventTypeBreakdown].sort((a, b) => b.count - a.count);
    const threshold = 0.02 * total;
    const main: { eventType: string; count: number; label: string }[] = [];
    let otherSum = 0;
    for (const r of rows) {
      if (r.count < threshold && main.length >= 3) {
        otherSum += r.count;
      } else {
        main.push({
          eventType: r.eventType,
          count: r.count,
          label: `${r.eventType} (${((r.count / total) * 100).toFixed(1)}%)`,
        });
      }
    }
    if (otherSum > 0) {
      main.push({
        eventType: '__other__',
        count: otherSum,
        label: `Other (${((otherSum / total) * 100).toFixed(1)}%)`,
      });
    }
    const col = getChartColors(isCb).info;
    const rowsForChart = [...main].reverse();
    const comp = this;
    this.eventTypeHorizData.set({
      labels: rowsForChart.map((r) => r.label),
      datasets: [
        {
          label: 'Events',
          data: rowsForChart.map((r) => r.count),
          backgroundColor: this.hexToRgba(col.primary, isDark ? 0.45 : 0.35),
          borderColor: col.border,
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    });
    this.eventTypeHorizOptions.set({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            afterLabel: (ctx: { dataIndex?: number }) => {
              const i = ctx.dataIndex ?? 0;
              const row = rowsForChart[i];
              return row ? `${row.count.toLocaleString()} events` : '';
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { color: text, precision: 0 },
          grid: { color: grid },
        },
        y: {
          ticks: { color: text, font: { size: 11 } },
          grid: { display: false },
        },
      },
      onClick: (_e: unknown, elements: { index: number }[]) => {
        if (!elements.length) {
          return;
        }
        const row = rowsForChart[elements[0].index];
        if (!row || row.eventType === '__other__') {
          return;
        }
        comp.navigateFilterEventType(row.eventType);
      },
    });
  }

  private buildPerformanceChart(
    d: WebAnalyticsDashboardData,
    isDark: boolean,
    isCb: boolean,
    text: string,
    grid: string,
  ): void {
    const rows = d.performanceByDay ?? [];
    if (!rows.length) {
      this.performanceChartData.set(null);
      this.performanceChartOptions.set(null);
      return;
    }
    const labels = rows.map((b) => b.date.slice(5));
    const c = getChartColors(isCb);
    this.performanceChartData.set({
      labels,
      datasets: [
        {
          label: 'Avg LCP (ms)',
          data: rows.map((b) => b.avgLcp ?? null),
          yAxisID: 'y',
          tension: 0.35,
          borderColor: c.warning.border,
          backgroundColor: this.hexToRgba(c.warning.primary, 0.08),
          pointRadius: 2,
          spanGaps: true,
        },
        {
          label: 'Avg FCP (ms)',
          data: rows.map((b) => b.avgFcp ?? null),
          yAxisID: 'y',
          tension: 0.35,
          borderColor: c.info.border,
          backgroundColor: this.hexToRgba(c.info.primary, 0.08),
          pointRadius: 2,
          spanGaps: true,
        },
        {
          label: 'Avg TTFB (ms)',
          data: rows.map((b) => b.avgTtfb ?? null),
          yAxisID: 'y',
          tension: 0.35,
          borderColor: c.success.border,
          backgroundColor: this.hexToRgba(c.success.primary, 0.08),
          pointRadius: 2,
          spanGaps: true,
        },
        {
          label: 'Avg CLS',
          data: rows.map((b) =>
            b.avgCls != null ? Math.round(b.avgCls * 1000) / 1000 : null,
          ),
          yAxisID: 'y1',
          tension: 0.35,
          borderColor: c.danger.border,
          backgroundColor: this.hexToRgba(c.danger.primary, 0.08),
          pointRadius: 2,
          spanGaps: true,
        },
      ],
    });
    this.performanceChartOptions.set({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { labels: { color: text, boxWidth: 12, font: { size: 11 } } },
      },
      scales: {
        x: { ticks: { color: text, maxRotation: 0 }, grid: { color: grid } },
        y: {
          type: 'linear',
          position: 'left',
          beginAtZero: true,
          ticks: { color: text },
          grid: { color: grid },
          title: { display: true, text: 'ms', color: text },
        },
        y1: {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          ticks: { color: text },
          grid: { drawOnChartArea: false },
          title: { display: true, text: 'CLS', color: text },
        },
      },
    });
  }

  private buildActivityMainChart(d: WebAnalyticsDashboardData): void {
    const isDark = document.documentElement.classList.contains('theme-dark');
    const isCb = document.documentElement.classList.contains('colorblind-mode');
    const text = isDark ? '#e5e7eb' : '#374151';
    const grid = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    const raw = d.eventsByDayByEventType ?? [];
    const mode = this.activityMode();
    const gran = this.activityGranularity();

    const { labels, matrix, types } = this.pivotEventTypes(raw, gran);
    if (!labels.length) {
      this.activityMainData.set(null);
      this.activityMainOptions.set(null);
      return;
    }

    const c = getChartColors(isCb);
    const keys = ['info', 'success', 'warning', 'danger', 'secondary'] as const;

    if (mode === 'total') {
      const totals = labels.map((_, i) =>
        types.reduce((s, t) => s + (matrix[t][i] ?? 0), 0),
      );
      this.activityMainData.set({
        labels,
        datasets: [
          {
            label: 'Total events',
            data: totals,
            fill: true,
            tension: 0.35,
            borderColor: c.info.border,
            backgroundColor: this.hexToRgba(c.info.primary, isDark ? 0.22 : 0.15),
          },
        ],
      });
    } else {
      const datasets = types.map((t, idx) => {
        const k = keys[idx % keys.length];
        const col = c[k];
        const data = matrix[t];
        return {
          label: t,
          data,
          fill: mode === 'stacked',
          tension: 0.35,
          stack: mode === 'stacked' ? 'a' : undefined,
          borderColor: col.border,
          backgroundColor: this.hexToRgba(
            col.primary,
            mode === 'stacked' ? (isDark ? 0.35 : 0.28) : 0.08,
          ),
          pointRadius: 2,
        };
      });
      this.activityMainData.set({ labels, datasets });
    }

    this.activityMainOptions.set({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          labels: {
            color: text,
            boxWidth: 12,
            padding: 8,
            font: { size: 11 },
            generateLabels: (chart: any) => {
              const data = chart.data;
              if (data.datasets.length) {
                return data.datasets.map((dataset: any, i: number) => ({
                  text: dataset.label.length > 24 ? dataset.label.substring(0, 23) + '…' : dataset.label,
                  fillStyle: dataset.backgroundColor,
                  strokeStyle: dataset.borderColor,
                  lineWidth: 1,
                  hidden: !chart.isDatasetVisible(i),
                  index: i,
                  datasetIndex: i,
                }));
              }
              return [];
            },
          },
          position: 'bottom',
          align: 'start',
          maxHeight: 80,
        },
        tooltip: {
          callbacks: {
            footer: (items: { datasetIndex?: number; dataIndex?: number; parsed?: { y: number } }[]) => {
              if (!items.length) {
                return '';
              }
              const i = items[0].dataIndex ?? 0;
              const parts = types.map((t) => {
                const v = matrix[t][i] ?? 0;
                return `${t}: ${v.toLocaleString()}`;
              });
              return parts.join('\n');
            },
          },
        },
      },
      scales: {
        x: {
          stacked: mode === 'stacked',
          ticks: { color: text, maxRotation: 0 },
          grid: { color: grid },
        },
        y: {
          stacked: mode === 'stacked',
          beginAtZero: true,
          ticks: { color: text, precision: 0 },
          grid: { color: grid },
        },
      },
    });

    if (gran === 'hourly') {
      /* schema has daily buckets only */
    }
  }

  private pivotEventTypes(
    raw: WebAnalyticsDailyEventTypeCount[],
    gran: Granularity,
  ): {
    labels: string[];
    matrix: Record<string, number[]>;
    types: string[];
  } {
    const typeSet = new Set<string>();
    for (const row of raw) {
      typeSet.add(row.eventType);
    }
    const allTypes = [...typeSet].sort();

    let dates: string[];
    if (gran === 'weekly') {
      const w = new Set<string>();
      for (const row of raw) {
        w.add(this.weekBucketKey(row.date));
      }
      dates = [...w].sort();
    } else {
      const dateSet = new Set<string>();
      for (const row of raw) {
        dateSet.add(row.date);
      }
      dates = [...dateSet].sort();
    }

    const matrix: Record<string, number[]> = {};
    for (const t of allTypes) {
      matrix[t] = dates.map(() => 0);
    }
    const idxMap = new Map(dates.map((dt, i) => [dt, i]));
    for (const row of raw) {
      const dk =
        gran === 'weekly' ? this.weekBucketKey(row.date) : row.date;
      const i = idxMap.get(dk);
      if (i === undefined) {
        continue;
      }
      matrix[row.eventType][i] += row.count;
    }
    const labels = dates.map((dt) => dt.slice(5));
    const topTypes = [...allTypes].sort((a, b) => {
      const sa = raw
        .filter((x) => x.eventType === a)
        .reduce((s, x) => s + x.count, 0);
      const sb = raw
        .filter((x) => x.eventType === b)
        .reduce((s, x) => s + x.count, 0);
      return sb - sa;
    });
    // Limit to 7 event types to prevent legend overflow
    const cap = 7;
    const use = topTypes.slice(0, cap);
    const narrowMatrix: Record<string, number[]> = {};
    for (const t of use) {
      narrowMatrix[t] = matrix[t];
    }
    return { labels, matrix: narrowMatrix, types: use };
  }

  private weekBucketKey(ymd: string): string {
    const d = new Date(ymd + 'T12:00:00Z');
    const day = d.getUTCDay();
    const diff = (day + 6) % 7;
    const monday = new Date(d.getTime() - diff * 86400000);
    return monday.toISOString().slice(0, 10);
  }

  private makeDoughnut(
    rows: WebAnalyticsBreakdownRow[],
    isCb: boolean,
    text: string,
    formatLabel?: (label: string) => string,
  ): { data: Record<string, unknown>; options: Record<string, unknown> } {
    const palette = this.expandedPalette(isCb, Math.max(rows.length, 1));
    const labelFor = formatLabel ?? ((label: string) => label);
    return {
      data: {
        labels: rows.map((r) => labelFor(r.label)),
        datasets: [
          {
            data: rows.map((r) => r.count),
            backgroundColor: palette.bg,
            hoverBackgroundColor: palette.hover,
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: { color: text, boxWidth: 10 },
          },
        },
      },
    };
  }

  private makeHorizontalBar(
    rows: WebAnalyticsBreakdownRow[],
    isCb: boolean,
    text: string,
    grid: string,
    colorKey: 'info' | 'success' | 'warning' | 'danger' | 'secondary',
  ): { data: Record<string, unknown>; options: Record<string, unknown> } {
    const col = getChartColors(isCb)[colorKey];
    const rev = [...rows].reverse();
    return {
      data: {
        labels: rev.map((r) =>
          r.label.length > 42 ? r.label.slice(0, 41) + '…' : r.label,
        ),
        datasets: [
          {
            label: 'Events',
            data: rev.map((r) => r.count),
            backgroundColor: this.hexToRgba(col.primary, 0.38),
            borderColor: col.border,
            borderWidth: 1,
            borderRadius: 4,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            beginAtZero: true,
            ticks: { color: text, precision: 0 },
            grid: { color: grid },
          },
          y: {
            ticks: { color: text, font: { size: 11 } },
            grid: { display: false },
          },
        },
      },
    };
  }

  private hexToRgba(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    const n = parseInt(
      h.length === 3 ? h.split('').map((x) => x + x).join('') : h,
      16,
    );
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  }

  private expandedPalette(
    isColorblind: boolean,
    n: number,
  ): { bg: string[]; hover: string[] } {
    return getCategoricalChartPalette(isColorblind, n);
  }

  private truncatePath(s: string, max: number): string {
    if (s.length <= max) {
      return s;
    }
    return s.slice(0, max - 1) + '…';
  }

  /** Expose for template tooltips */
  truncatePathUi(s: string, max: number): string {
    return truncatePathMiddle(s, max);
  }
}
